// Dashboard component - Main interface for affiliate management
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Card,
  CardBody,
  Input,
  Textarea,
  FormControl,
  FormLabel,
  VStack,
  HStack,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useToast,
  useDisclosure,
  Divider,
  Grid,
  Image,
  Spinner,
  Container,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  GridItem,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Select,
  Badge,
  Tooltip,
  Switch,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Alert,
  AlertIcon,
  Progress,
  SimpleGrid,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel
} from '@chakra-ui/react';
import {
  Trash2 as DeleteIcon, 
  Edit2 as EditIcon, 
  Plus as AddIcon, 
  Settings as SettingsIcon, 
  BarChart3, 
  MessageCircle, 
  ExternalLink, 
  Search,
  Eye,
  Download,
  Copy,
  TrendingUp,
  Users,
  Clock,
  Target,
  Activity,
  HelpCircle,
  ChevronDown as ExpandMoreIcon,
  LogOut as LogoutIcon,
  FileText,
  BookOpen,
  Brain,
  Zap,
  Code,
  Info 
} from 'lucide-react';
import { 
  EditIcon as ChakraEditIcon,
  DeleteIcon as ChakraDeleteIcon,
  AddIcon as ChakraAddIcon,
  ExternalLinkIcon as ChakraExternalLinkIcon,
  CopyIcon as ChakraCopyIcon,
  SettingsIcon as ChakraSettingsIcon,
  ChatIcon
} from '@chakra-ui/icons';
// ChatWidget import removed - now using iframe approach for consistency
import { supabase } from '../supabaseClient';
import { BASE_INSTRUCTIONS } from '../instructions.ts';

const drawerWidth = 240;

const navItems = [
  { label: 'Offer Links', icon: <ExternalLink /> },
  { label: 'Training Materials', icon: <FileText /> },
  { label: 'Chat Settings', icon: <SettingsIcon /> },
  { label: 'Instructions', icon: <Info /> },
  { label: 'Embed Widget', icon: <Code /> },
  { label: 'Analytics', icon: <BarChart3 /> },
  { label: 'Chat Logs', icon: <MessageCircle /> },
];

// Use environment variable with fallback to Render API
const API_URL = process.env.REACT_APP_API_URL || 'https://choosly.onrender.com';

console.log('API_URL is:', API_URL); // Debug log to verify it's working
console.log('All env vars:', {
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  REACT_APP_SUPABASE_URL: process.env.REACT_APP_SUPABASE_URL
}); // Debug log to see all env vars

// Default instructions template removed - now using BASE_INSTRUCTIONS from ../instructions.ts

function Dashboard({ session, onSignOut, shouldOpenChat, widgetSiteId, isEmbedded }) {
  const [selectedTab, setSelectedTab] = useState(0);
  const [trainingMaterials, setTrainingMaterials] = useState([]);
  const [affiliateLinks, setAffiliateLinks] = useState([]);
  const [newTrainingUrl, setNewTrainingUrl] = useState('');
  const [newLink, setNewLink] = useState({
    url: '',
    name: '',
    description: '',
    image_url: '',
    button_text: 'Learn more'
  });
  const [editingLink, setEditingLink] = useState(null);
  const [chatSettings, setChatSettings] = useState({
    chat_name: 'Affi',
    chat_color: '#000000',
    chat_icon_url: '',
    chat_name_color: '#FFFFFF',
    chat_bubble_icon_color: '#FFFFFF',
    input_placeholder: 'Type your message...',
    font_size: '14px'
  });
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [newSiteName, setNewSiteName] = useState('');
  const [introMessage, setIntroMessage] = useState('Hello! How can I help you today?');
  const [isSaving, setIsSaving] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const [chatSessions, setChatSessions] = useState([]);

  // Add all missing state variables
  const [instructions, setInstructions] = useState(BASE_INSTRUCTIONS);
  const [chatLogs, setChatLogs] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [chatStats, setChatStats] = useState(null);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isLoadingAnalytics, setIsLoadingAnalytics] = useState(false);
  const [editSiteName, setEditSiteName] = useState('');
  const [siteDialogOpen, setSiteDialogOpen] = useState(false);
  const [editLinkDialogOpen, setEditLinkDialogOpen] = useState(false);

  // Component lifecycle management
  const isMountedRef = useRef(true);
  const timersRef = useRef([]);
  const abortControllerRef = useRef(null);

  const toast = useToast();

  // Helper functions for dialog management
  const openSiteDialog = (site = null) => {
    setEditingSite(site);
    setEditSiteName(site ? site.name : '');
    setSiteDialogOpen(true);
  };

  const closeSiteDialog = () => {
    setSiteDialogOpen(false);
    setEditingSite(null);
    setEditSiteName('');
  };

  const closeEditLinkDialog = () => {
    setEditLinkDialogOpen(false);
    setEditingLink(null);
  };

  const showToast = useCallback((message, type = 'success') => {
    toast({
      title: message,
      status: type,
      duration: 3000,
      isClosable: true,
    });
  }, [toast]);

  // Safe state update function
  const safeSetState = useCallback((setter, value) => {
    if (isMountedRef.current) {
      setter(value);
    }
  }, []);

  // Helper function to manage timers
  const safeSetTimeout = useCallback((callback, delay) => {
    if (!isMountedRef.current) return null;
    
    const timerId = setTimeout(() => {
      if (isMountedRef.current) {
        timersRef.current = timersRef.current.filter(id => id !== timerId);
        callback();
      }
    }, delay);
    
    timersRef.current.push(timerId);
    return timerId;
  }, []);

  // Define all async functions with useCallback to prevent infinite re-renders
  const loadChatSettings = useCallback(async () => {
    if (!selectedSite?.id || !isMountedRef.current) return;
    
    try {
      const { data, error } = await supabase
        .from('chat_settings')
        .select('*')
        .eq('site_id', selectedSite.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Error loading chat settings:', error);
        return;
      }

      if (data && isMountedRef.current) {
        safeSetState(setChatSettings, {
          chat_name: data.chat_name || '',
          chat_color: data.chat_color || '#000000',
          chat_icon_url: data.chat_icon_url || '',
          chat_name_color: data.chat_name_color || '#FFFFFF',
          chat_bubble_icon_color: data.chat_bubble_icon_color || '#FFFFFF',
          input_placeholder: data.input_placeholder || 'Type your message...',
          font_size: data.font_size || '14px'
        });
        safeSetState(setIntroMessage, data.intro_message || 'Hi! I am Affi, your assistant. How can I help you today?');
      } else if (isMountedRef.current) {
        // Set defaults if no settings found
        safeSetState(setChatSettings, {
          chat_name: '',
          chat_color: '#000000',
          chat_icon_url: '',
          chat_name_color: '#FFFFFF',
          chat_bubble_icon_color: '#FFFFFF',
          input_placeholder: 'Type your message...',
          font_size: '14px'
        });
        safeSetState(setIntroMessage, 'Hi! I am Affi, your assistant. How can I help you today?');
      }
    } catch (error) {
      console.error('Error in loadChatSettings:', error);
    }
  }, [selectedSite?.id, safeSetState]);

  const fetchAffiliateLinks = useCallback(async () => {
    if (!selectedSite?.id || !session?.user || !isMountedRef.current) return;
    
    try {
      const { data, error } = await supabase
        .from('affiliate_links')
        .select('*')
        .eq('site_id', selectedSite.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching affiliate links:', error);
        return;
      }

      if (isMountedRef.current) {
        safeSetState(setAffiliateLinks, data || []);
      }
    } catch (error) {
      console.error('Error in fetchAffiliateLinks:', error);
    }
  }, [selectedSite?.id, session?.user, safeSetState]);

  const fetchSites = useCallback(async () => {
    if (!session?.user || !isMountedRef.current) return;
    
    try {
      const { data, error } = await supabase
        .from('sites')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching sites:', error);
        return;
      }

      if (isMountedRef.current) {
        safeSetState(setSites, data || []);
      
        // If no site is selected and we have sites, select the first one
        if (data && data.length > 0 && !selectedSite) {
          safeSetState(setSelectedSite, data[0]);
        }
      }
    } catch (error) {
      console.error('Error in fetchSites:', error);
    }
  }, [session?.user, selectedSite, safeSetState]);

  const fetchTrainingMaterials = useCallback(async () => {
    if (!selectedSite?.id || !isMountedRef.current) return;
    
    try {
      const { data, error } = await supabase
        .from('training_materials')
        .select('*')
        .eq('site_id', selectedSite.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching training materials:', error);
        return;
      }

      if (isMountedRef.current) {
        safeSetState(setTrainingMaterials, data || []);
      }
    } catch (error) {
      console.error('Error in fetchTrainingMaterials:', error);
    }
  }, [selectedSite?.id, safeSetState]);

  const fetchChatLogs = useCallback(async (sessionId) => {
    if (!sessionId || !isMountedRef.current) return;
    
    try {
      safeSetState(setIsLoadingLogs, true);
      
      const { data, error } = await supabase
        .from('chat_logs')
        .select('*')
        .eq('session_id', sessionId)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('Error fetching chat logs:', error);
        showToast('Failed to load chat logs', 'error');
        return;
      }

      if (isMountedRef.current) {
        safeSetState(setChatLogs, data || []);
      }
    } catch (error) {
      console.error('Error in fetchChatLogs:', error);
      showToast('Failed to load chat logs', 'error');
    } finally {
      if (isMountedRef.current) {
        safeSetState(setIsLoadingLogs, false);
      }
    }
  }, [safeSetState, showToast]);

  const fetchInstructions = useCallback(async () => {
    try {
      if (!isMountedRef.current) return;
      
      // Simply use the hardcoded DEFAULT_INSTRUCTIONS
      // If we have a selected site, try to get custom instructions from chat_settings
      if (selectedSite) {
        const { data: chatSettings, error } = await supabase
        .from('chat_settings')
          .select('instructions')
          .eq('site_id', selectedSite.id)
          .eq('user_id', session.user.id)
          .single();
        
        if (!error && chatSettings?.instructions) {
          safeSetState(setInstructions, chatSettings.instructions);
        } else {
          // Use hardcoded default instructions
          safeSetState(setInstructions, BASE_INSTRUCTIONS);
        }
      } else {
        // Use hardcoded default instructions
        safeSetState(setInstructions, BASE_INSTRUCTIONS);
      }
    } catch (error) {
      console.error('Error in fetchInstructions:', error);
      // Fallback to hardcoded instructions on any error
      if (isMountedRef.current) {
        safeSetState(setInstructions, BASE_INSTRUCTIONS);
      }
    }
  }, [selectedSite, session?.user, safeSetState]);

  const fetchChatSessions = useCallback(async () => {
    if (!selectedSite?.id || !session?.user || !isMountedRef.current) return;
    
    try {
      // Try using RPC function first (from migration)
      const { data: rpcData, error: rpcError } = await supabase
        .rpc('get_chat_sessions', { 
          site_id_param: selectedSite.id,
          user_id_param: session.user.id 
        });
      
      if (!rpcError && rpcData && isMountedRef.current) {
        safeSetState(setChatSessions, rpcData);
        return;
      }
      
      // Fallback to direct table query
      const { data, error } = await supabase
        .from('chat_logs')
        .select('session_id, site_id, created_at, user_message')
        .eq('site_id', selectedSite.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching chat sessions:', error);
        return;
      }

      // Group by session_id and get latest message for each session
      const sessionsMap = new Map();
      data?.forEach(log => {
        if (!sessionsMap.has(log.session_id) || 
            new Date(log.created_at) > new Date(sessionsMap.get(log.session_id).last_message_at)) {
          sessionsMap.set(log.session_id, {
            session_id: log.session_id,
            site_id: log.site_id,
            last_message_at: log.created_at,
            preview: log.user_message?.substring(0, 50) + '...'
          });
        }
      });

      if (isMountedRef.current) {
        safeSetState(setChatSessions, Array.from(sessionsMap.values()));
      }
    } catch (error) {
      console.error('Error in fetchChatSessions:', error);
    }
  }, [selectedSite?.id, session?.user, safeSetState]);

  const fetchChatStats = useCallback(async () => {
    if (!selectedSite?.id || !isMountedRef.current) return;
    
    try {
      // Try using RPC function first (from migration)
      const { data: dailyData, error: dailyError } = await supabase
        .rpc('get_daily_chat_stats', { site_id_param: selectedSite.id });
      
      // Get overall stats from chat_logs table
      const { data: statsData, error: statsError } = await supabase
        .from('chat_logs')
        .select('session_id, created_at, links_shown')
        .eq('site_id', selectedSite.id);

      if (statsError) {
        console.error('Error fetching chat stats:', statsError);
        return;
      }

      // Calculate stats
      const uniqueSessions = new Set(statsData?.map(log => log.session_id));
      const totalLinks = statsData?.reduce((sum, log) => {
        const links = log.links_shown ? JSON.parse(log.links_shown).length : 0;
        return sum + links;
      }, 0) || 0;

      const stats = {
        total_conversations: uniqueSessions.size,
        total_messages: statsData?.length || 0,
        total_links_shown: totalLinks,
        daily_stats: dailyData || []
      };

      if (isMountedRef.current) {
        safeSetState(setChatStats, stats);
      }
    } catch (error) {
      console.error('Error in fetchChatStats:', error);
    }
  }, [selectedSite?.id, safeSetState]);

  const fetchAnalytics = useCallback(async () => {
    if (!selectedSite?.id || !isMountedRef.current) return;
    
    try {
      safeSetState(setIsLoadingAnalytics, true);
      
          const { data, error } = await supabase
        .from('chat_logs')
            .select('*')
        .eq('site_id', selectedSite.id)
        .order('created_at', { ascending: false });

          if (error) {
        console.error('Error fetching analytics:', error);
            return;
          }

      // Process analytics data
      const processed = {
        total_conversations: new Set(data?.map(log => log.session_id)).size,
        total_messages: data?.length || 0,
        link_clicks: data?.reduce((sum, log) => {
          if (log.links_shown) {
            try {
              const links = JSON.parse(log.links_shown);
              return sum + links.length;
            } catch (e) {
              return sum;
            }
          }
          return sum;
        }, 0) || 0,
        daily_conversations: [],
        popular_links: [],
        recent_sessions: data?.slice(0, 10) || []
      };

      if (isMountedRef.current) {
        safeSetState(setAnalyticsData, processed);
          }
        } catch (error) {
      console.error('Error in fetchAnalytics:', error);
    } finally {
      if (isMountedRef.current) {
        safeSetState(setIsLoadingAnalytics, false);
        }
    }
  }, [selectedSite?.id, safeSetState]);

  // Cleanup function
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      timersRef.current.forEach(timer => clearTimeout(timer));
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Initialize data with proper cleanup
  const initializeData = useCallback(async () => {
    if (!session?.user || !isMountedRef.current) return;

    try {
      // Fetch sites
      const { data: sitesData, error: sitesError } = await supabase
        .from('sites')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false });

      if (sitesError) throw sitesError;
      safeSetState(setSites, sitesData || []);

      // If no site is selected and we have sites, select the first one
      if (!selectedSite && sitesData?.length > 0) {
        safeSetState(setSelectedSite, sitesData[0]);
        return; // Return here as the selectedSite change will trigger another initialization
      }

      // Only proceed with these fetches if we have a selected site
      if (selectedSite) {
        // Fetch affiliate links
        const { data: linksData, error: linksError } = await supabase
          .from('affiliate_links')
          .select('*')
          .eq('site_id', selectedSite.id)
          .order('created_at', { ascending: false });

        if (linksError) throw linksError;
        safeSetState(setAffiliateLinks, linksData || []);

        // Fetch chat settings
        const { data: settingsData, error: settingsError } = await supabase
          .from('chat_settings')
          .select('*')
          .eq('site_id', selectedSite.id)
          .single();

        if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

        if (settingsData) {
          safeSetState(setChatSettings, prev => ({
            ...prev,
            ...settingsData
          }));
        }
      }
    } catch (error) {
      console.error('Error initializing data:', error);
      // Only show toast for non-network errors
      if (!error.message?.includes('Failed to fetch')) {
        toast({
          title: 'Error loading data',
          description: error.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    }
  }, [session?.user, selectedSite, safeSetState, toast]);

  // Single effect for initialization with debouncing
  useEffect(() => {
    if (!session?.user) return;

    let timeoutId;
    const debouncedInit = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(() => {
        if (isMountedRef.current) {
          initializeData();
        }
      }, 500); // 500ms debounce
    };

    debouncedInit();

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [session?.user, selectedSite, initializeData]);

  // Effect for chat widget
  useEffect(() => {
    if (shouldOpenChat && selectedSite) {
      const timer = setTimeout(() => {
        if (isMountedRef.current) {
          window.dispatchEvent(new CustomEvent('open-chat-widget'));
        }
      }, 1000);
      timersRef.current.push(timer);
    }
  }, [shouldOpenChat, selectedSite]);

  const handleSaveChatSettings = async () => {
    if (!selectedSite?.id || !session?.user || !isMountedRef.current) return;
    
    try {
      safeSetState(setIsSaving, true);
      
      const settingsData = {
        site_id: selectedSite.id,
        user_id: session.user.id,
        chat_name: chatSettings.chat_name || '',
        chat_color: chatSettings.chat_color || '#000000',
        chat_icon_url: chatSettings.chat_icon_url || '',
        chat_name_color: chatSettings.chat_name_color || '#FFFFFF',
        chat_bubble_icon_color: chatSettings.chat_bubble_icon_color || '#FFFFFF',
        input_placeholder: chatSettings.input_placeholder || 'Type your message...',
        intro_message: introMessage || 'Hi! I am Affi, your assistant. How can I help you today?',
        font_size: chatSettings.font_size || '14px'
      };

      const { error } = await supabase
        .from('chat_settings')
        .upsert([settingsData], {
          onConflict: 'site_id'
        });

      if (error) {
        console.error('Error saving chat settings:', error);
        showToast('Failed to save chat settings', 'error');
        return;
      }

      showToast('Chat settings saved successfully');
    } catch (error) {
      console.error('Error in handleSaveChatSettings:', error);
      showToast('Failed to save chat settings', 'error');
    } finally {
      if (isMountedRef.current) {
        safeSetState(setIsSaving, false);
      }
    }
  };


  const handleEditSite = (site) => {
    setEditingSite(site);
    setEditSiteName(site.name);
  };

  const handleCloseSiteDialog = () => {
    setEditingSite(null);
    setEditSiteName('');
    setNewSiteName('');
    closeSiteDialog();
  };

  const handleUpdateSite = async () => {
    if (!editingSite || !newSiteName.trim()) return;
    
    try {
      const { error } = await supabase
        .from('sites')
        .update({ name: newSiteName.trim() })
        .eq('id', editingSite.id)
        .eq('user_id', session.user.id);
      
      if (error) throw error;
      
      await fetchSites();
      setEditingSite(null);
      setNewSiteName('');
      showToast('Site updated successfully!');
    } catch (error) {
      console.error('Error updating site:', error);
      showToast('Error updating site', 'error');
    }
  };

  const handleSaveInstructions = async () => {
    if (!selectedSite) {
      showToast('Please select a site first', 'error');
      return;
    }

    try {
      setIsSaving(true);
      
      // Save instructions to the chat_settings table
      const { error } = await supabase
        .from('chat_settings')
        .upsert({
          site_id: selectedSite.id,
          user_id: session.user.id,
          instructions: instructions,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      showToast('Instructions saved successfully!');
    } catch (error) {
      console.error('Error saving instructions:', error);
      showToast('Failed to save instructions', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddNewLink = async () => {
    if (!selectedSite) {
      showToast('Please select a site first', 'error');
      return;
    }
    
    try {
      setIsSaving(true);
      const linkData = {
        url: newLink.url,
        name: newLink.name,
        description: newLink.description || '',
        image_url: newLink.image_url || '',
        user_id: session.user.id,
        site_id: selectedSite.id,
        button_text: newLink.button_text && newLink.button_text.trim() ? newLink.button_text : 'Learn more',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };

      const { error } = await supabase
        .from('affiliate_links')
        .insert([linkData]);
      
      if (error) throw error;
      
      // Reset form
      setNewLink({
        url: '',
        name: '',
        description: '',
        image_url: '',
        button_text: 'Learn more'
      });
      
      await fetchAffiliateLinks();
      showToast('Affiliate link added successfully!');
    } catch (error) {
      console.error('Error adding affiliate link:', error);
      showToast('Error adding affiliate link', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEditLink = (link) => {
    setEditingLink(link);
    setNewLink({
      url: link.url,
      name: link.name,
      description: link.description,
      image_url: link.image_url || '',
      button_text: link.button_text || 'Learn more'
    });
    setEditLinkDialogOpen(true);
  };

  const handleUpdateLink = async () => {
    if (!editingLink) return;
    
    try {
      const { error } = await supabase
        .from('affiliate_links')
        .update(newLink)
        .eq('id', editingLink.id)
        .eq('user_id', session.user.id);

      if (error) throw error;
      
      await fetchAffiliateLinks();
      setEditingLink(null);
      setNewLink({
        url: '',
        name: '',
        description: '',
        image_url: '',
        button_text: 'Learn more'
      });
      showToast('Affiliate link updated successfully!');
    } catch (error) {
      console.error('Error updating affiliate link:', error);
      showToast('Error updating affiliate link', 'error');
    }
  };

  const handleDeleteAffiliateLink = async (linkId) => {
    try {
      const { error } = await supabase
        .from('affiliate_links')
        .delete()
        .eq('id', linkId)
        .eq('user_id', session.user.id);

      if (error) throw error;
      await fetchAffiliateLinks();
      showToast('Affiliate link deleted successfully!');
    } catch (error) {
      console.error('Error deleting affiliate link:', error);
      showToast('Error deleting affiliate link', 'error');
    }
  };

  const handleAddTrainingMaterial = async (materialData) => {
    if (!selectedSite) {
      showToast('Please select a site first', 'error');
      return;
    }

    if (!materialData.url || !materialData.url.trim()) {
      showToast('Please enter a valid URL', 'error');
      return;
    }

    try {
      setIsSaving(true);
      showToast('Scraping website content...', 'info');

      // Call the backend API to scrape and store the content
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/training`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-user-id': session.user.id,
          'x-site-id': selectedSite.id
        },
        body: JSON.stringify({
          url: materialData.url.trim()
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to scrape website');
      }

      const result = await response.json();
      
      if (!result.content || result.content.trim() === '') {
        throw new Error('No content could be extracted from the URL');
      }

      setNewTrainingUrl(''); // Clear the input
      await fetchTrainingMaterials();
      showToast('Training material added successfully! The AI will now use this content when answering questions.', 'success');
    } catch (error) {
      console.error('Error adding training material:', error);
      showToast('Error adding training material: ' + error.message, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTrainingMaterial = async (materialId) => {
      if (!selectedSite) {
      showToast('Please select a site first', 'error');
        return;
      }

    try {
      const response = await fetch(`${API_URL}/api/training/${materialId}`, {
        method: 'DELETE',
        headers: {
          'x-user-id': session.user.id,
          'x-site-id': selectedSite.id
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || 'Failed to delete training material');
      }
      
      await fetchTrainingMaterials();
      showToast('Training material deleted successfully!');
    } catch (error) {
      console.error('Error deleting training material:', error);
      showToast('Error deleting training material: ' + error.message, 'error');
    }
  };

  const handleDeleteSite = async (siteId) => {
    try {
      const { error } = await supabase
        .from('sites')
        .delete()
        .eq('id', siteId)
        .eq('user_id', session.user.id);

      if (error) throw error;
      await fetchSites();
      if (selectedSite?.id === siteId) {
        setSelectedSite(null);
      }
      showToast('Site deleted successfully!');
    } catch (error) {
      console.error('Error deleting site:', error);
      showToast('Error deleting site', 'error');
    }
  };

  const handleCreateSite = async () => {
    if (!newSiteName.trim()) {
      showToast('Site name is required', 'error');
      return;
    }
    
    try {
      const { data, error } = await supabase
        .from('sites')
        .insert([{
          name: newSiteName.trim(),
          user_id: session.user.id
        }])
        .select()
        .single();

      if (error) throw error;
      
      // Create default chat settings for the new site
      await supabase
        .from('chat_settings')
        .insert([{
          user_id: session.user.id,
          site_id: data.id,
          chat_name: 'Affi',
          chat_color: '#000000',
          chat_icon_url: '',
          chat_name_color: '#FFFFFF',
          chat_bubble_icon_color: '#FFFFFF',
          input_placeholder: 'Type your message...',
          intro_message: 'Hello! How can I help you today?',
          font_size: '14px',
          instructions: BASE_INSTRUCTIONS
        }]);
      
      await fetchSites();
      setNewSiteName('');
      showToast('Site created successfully!');
    } catch (error) {
      console.error('Error creating site:', error);
      showToast('Error creating site', 'error');
    }
  };

  // Widget mode - show only chat
  if (shouldOpenChat && selectedSite) {
    // Embedded mode (in iframe) - minimal UI
    if (isEmbedded) {
      return (
        <Box minH="100vh" bg="white" p={0} m={0}>
          <iframe
            key={`embedded-${selectedSite.id}`}
            src={`/widget-frame.html?siteId=${selectedSite.id}&apiUrl=${encodeURIComponent(API_URL)}&embedded=true`}
            title="Embedded Chat Widget"
            style={{
              width: '100%',
              height: '100vh',
              border: 'none',
              background: 'transparent'
            }}
            allow="clipboard-write"
            sandbox="allow-scripts allow-same-origin allow-forms"
          />
        </Box>
      );
    }
    
    // Regular widget mode (popup window)
    return (
      <>
        <Box minH="100vh" bg="linear-gradient(135deg, #f5f7fa 0%, #e4e8f0 100%)">
          <iframe
            key={`widget-${selectedSite.id}`}
            src={`/widget-frame.html?siteId=${selectedSite.id}&apiUrl=${encodeURIComponent(API_URL)}`}
            title="Chat Widget"
            style={{
              width: '100%',
              height: '100vh',
              border: 'none',
              background: 'transparent'
            }}
            allow="clipboard-write"
            sandbox="allow-scripts allow-same-origin allow-forms"
          />
        </Box>
      </>
    );
  }

  return (
    <>
      <Flex minH="100vh" bg="#F9FAFB">
        {/* Sidebar */}
        <Box
          w={drawerWidth}
          bg="white"
          borderRight="1px solid #E8EAED"
          display="flex"
          flexDirection="column"
          overflow="hidden"
          h="100vh"
          position="fixed"
          left={0}
          top={0}
          zIndex={1000}
        >
          {/* Header */}
          <Flex
            align="center"
            justify="center"
            px={4}
            py={5}
            borderBottom="1px solid #E8EAED"
            bg="white"
          >
            <img src="/offerbase-logo.svg" alt="Offerbase Logo" style={{ height: 24 }} />
          </Flex>

          <VStack flex={1} overflow="auto" px={3} pt={4} spacing={1} align="stretch">
            <Text
              color="#9CA3AF"
              fontWeight="500"
              letterSpacing="0.5px"
              ml={1}
              mb={2}
              textTransform="uppercase"
              fontSize="11px"
            >
              Workspace
            </Text>
            <Button
              variant="ghost"
              onClick={() => openSiteDialog()}
              borderRadius="8px"
              color="#374151"
              bg="transparent"
              _hover={{ bg: '#F3F4F6' }}
              fontWeight="500"
              px={3}
              py={2.5}
              h="auto"
              justifyContent="flex-start"
              fontSize="14px"
              leftIcon={<ExpandMoreIcon size={16} />}
              mb={3}
            >
              {selectedSite ? selectedSite.name : 'Select Site'}
            </Button>
            
            <Text
              color="#9CA3AF"
              fontWeight="500"
              letterSpacing="0.5px"
              ml={1}
              mb={2}
              textTransform="uppercase"
              fontSize="11px"
            >
              Menu
            </Text>
            
            {navItems.map((item, index) => (
              <Button
                key={item.label}
                variant="ghost"
                onClick={() => setSelectedTab(index)}
                borderRadius="8px"
                color={selectedTab === index ? "#1F2937" : "#6B7280"}
                bg={selectedTab === index ? '#F3F4F6' : 'transparent'}
                _hover={{ bg: '#F3F4F6', color: '#1F2937' }}
                fontWeight="500"
                px={3}
                py={2.5}
                h="auto"
                justifyContent="flex-start"
                fontSize="14px"
                leftIcon={React.cloneElement(item.icon, { size: 16 })}
                transition="all 0.15s ease"
              >
                {item.label}
              </Button>
            ))}
          </VStack>
          
          {/* User Info Footer */}
          <Box
            px={3}
            py={4}
            borderTop="1px solid #E8EAED"
            bg="white"
          >
            <Text fontSize="13px" color="#6B7280" fontWeight="500" noWrap mb={2}>
              {session?.user?.email}
            </Text>
            <Button
              variant="ghost"
              onClick={onSignOut}
              size="sm"
              color="#6B7280"
              _hover={{ bg: '#F3F4F6', color: '#374151' }}
              borderRadius="6px"
              px={2}
              py={1}
              h="32px"
              fontSize="13px"
              fontWeight="500"
              leftIcon={<LogoutIcon size={14} />}
              w="full"
              justifyContent="flex-start"
            >
              Logout
            </Button>
          </Box>
        </Box>
        {/* Main Content */}
        <Box flex={1} ml={drawerWidth} p={{ base: 1, md: 2 }}>
          {/* Content Panels */}
          <Box w="100%">
            <Card
              bg="white"
              border="1px solid #E8EAED"
              borderRadius="12px"
              p={{ base: 1.5, md: 2 }}
            >
              <CardBody>
                {/* Offer Links Tab */}
                {selectedTab === 0 && (
                  <VStack spacing={6} align="stretch">
                    <Text fontSize="xl" fontWeight="semibold" color="#000" mb={6}>
                      Add New Offer Link
                    </Text>
                        <VStack spacing={4} align="stretch">
                          <FormControl>
                            <FormLabel>URL</FormLabel>
                            <Input
                              value={newLink.url}
                              onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                              bg="rgba(255, 255, 255, 0.8)"
                              border="1px solid rgba(0, 0, 0, 0.1)"
                              _focus={{
                                borderColor: "rgba(0, 0, 0, 0.3)",
                                boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                              }}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel>Title</FormLabel>
                            <Input
                              value={newLink.name}
                              onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                              bg="rgba(255, 255, 255, 0.8)"
                              border="1px solid rgba(0, 0, 0, 0.1)"
                              _focus={{
                                borderColor: "rgba(0, 0, 0, 0.3)",
                                boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                              }}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel>Description</FormLabel>
                            <Textarea
                              value={newLink.description}
                              onChange={(e) => setNewLink({ ...newLink, description: e.target.value })}
                              rows={2}
                              bg="rgba(255, 255, 255, 0.8)"
                              border="1px solid rgba(0, 0, 0, 0.1)"
                              _focus={{
                                borderColor: "rgba(0, 0, 0, 0.3)",
                                boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                              }}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel>Image URL (optional)</FormLabel>
                            <Input
                              value={newLink.image_url}
                              onChange={(e) => setNewLink({ ...newLink, image_url: e.target.value })}
                              bg="rgba(255, 255, 255, 0.8)"
                              border="1px solid rgba(0, 0, 0, 0.1)"
                              _focus={{
                                borderColor: "rgba(0, 0, 0, 0.3)",
                                boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                              }}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel>Link Button Text</FormLabel>
                            <Input
                              value={newLink.button_text}
                              onChange={(e) => setNewLink({ ...newLink, button_text: e.target.value })}
                              bg="rgba(255, 255, 255, 0.8)"
                              border="1px solid rgba(0, 0, 0, 0.1)"
                              _focus={{
                                borderColor: "rgba(0, 0, 0, 0.3)",
                                boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                              }}
                            />
                          </FormControl>
                          <Button
                            onClick={handleAddNewLink}
                            leftIcon={<AddIcon />}
                            bg="#000"
                            color="#fff"
                            _hover={{ bg: '#333' }}
                            maxW="200px"
                            alignSelf="flex-start"
                            isLoading={isSaving}
                            loadingText="Saving..."
                            fontWeight={500}
                            fontSize="14px"
                            px={6}
                          >
                            Add Link
                          </Button>
                        </VStack>
                    
                    <Text fontSize="lg" fontWeight="semibold" color="#000" mt={8}>
                      Your Offer Links
                    </Text>
                    <VStack spacing={4} align="stretch">
                      {affiliateLinks.map((link) => (
                        <Card
                          key={link.id}
                          bg="white"
                          border="1px solid #E8EAED"
                          borderRadius="12px"
                        >
                          <CardBody>
                            <Flex gap={4} align="flex-start">
                              {link.image_url && (
                                <Box
                                  w="100px"
                                  h="100px"
                                  flexShrink={0}
                                  borderRadius="10px"
                                  overflow="hidden"
                                  boxShadow="0 2px 8px 0 rgba(31,38,135,0.06)"
                                >
                                  <img
                                    src={link.image_url}
                                    alt={link.name}
                                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                  />
                                </Box>
                              )}
                              <Box flex={1}>
                                <Text fontSize="lg" fontWeight="semibold" color="#111" mb={2}>
                                  {link.name}
                                </Text>
                                <Text color="#444" mb={3}>
                                  {link.description}
                                </Text>
                                <HStack spacing={2}>
                                  <Button
                                    size="sm"
                                    onClick={() => handleEditLink(link)}
                                    bg="#000"
                                    color="#fff"
                                    _hover={{ bg: '#333' }}
                                    borderRadius="8px"
                                    fontWeight={500}
                                    fontSize="14px"
                                    px={4}
                                  >
                                    Edit
                                  </Button>
                                  <Button
                                    size="sm"
                                    leftIcon={<DeleteIcon />}
                                    onClick={() => handleDeleteAffiliateLink(link.id)}
                                    bg="#000"
                                    color="#fff"
                                    _hover={{ bg: '#333' }}
                                    borderRadius="8px"
                                    fontWeight={500}
                                    fontSize="14px"
                                    px={4}
                                  >
                                    Delete
                                  </Button>
                                </HStack>
                              </Box>
                            </Flex>
                          </CardBody>
                        </Card>
                      ))}
                    </VStack>
                  </VStack>
                )}

                {/* Training Materials Tab */}
                {selectedTab === 1 && (
                  <VStack spacing={6} align="stretch">
                    <Text fontSize="xl" fontWeight="semibold" color="#000" mb={6}>
                      Add New Training Material
                    </Text>
                        <VStack spacing={4} align="stretch">
                          <FormControl>
                            <FormLabel>Website URL</FormLabel>
                            <Input
                              value={newTrainingUrl}
                              onChange={(e) => setNewTrainingUrl(e.target.value)}
                              placeholder="https://example.com/page-to-scrape"
                              bg="rgba(255, 255, 255, 0.8)"
                              border="1px solid rgba(0, 0, 0, 0.1)"
                              _focus={{
                                borderColor: "rgba(0, 0, 0, 0.3)",
                                boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                              }}
                            />
                          </FormControl>
                          <Text fontSize="sm" color="gray.600">
                            Enter a URL to scrape and add to your AI assistant's training data.
                          </Text>
                          <Button
                            onClick={() => handleAddTrainingMaterial({ url: newTrainingUrl })}
                            leftIcon={<AddIcon />}
                            bg="#000"
                            color="#fff"
                            _hover={{ bg: '#333' }}
                            maxW="200px"
                            alignSelf="flex-start"
                            isLoading={isSaving}
                            loadingText="Scraping..."
                            fontWeight={500}
                            fontSize="14px"
                            px={6}
                          >
                            Add Training Material
                          </Button>
                        </VStack>
                    
                    <Text fontSize="lg" fontWeight="semibold" color="#000" mt={8}>
                      Your Training Materials
                    </Text>
                    <VStack spacing={4} align="stretch">
                      {trainingMaterials.map((material) => (
                        <Card
                          key={material.id}
                          bg="white"
                          border="1px solid #E8EAED"
                          borderRadius="12px"
                        >
                          <CardBody>
                            <Flex justify="space-between" align="flex-start" mb={3}>
                              <Box flex={1}>
                                <Text fontSize="lg" fontWeight="semibold" color="#111" mb={2}>
                                  {material.title || material.url}
                                </Text>
                                {material.url && (
                                  <Text fontSize="sm" color="blue.600" mb={2}>
                                    {material.url}
                                  </Text>
                                )}
                                <Text color="#444" fontSize="sm">
                                  Added: {new Date(material.created_at).toLocaleDateString()}
                                </Text>
                              </Box>
                              <Button
                                size="sm"
                                leftIcon={<DeleteIcon />}
                                onClick={() => handleDeleteTrainingMaterial(material.id)}
                                bg="#000"
                                color="#fff"
                                _hover={{ bg: '#333' }}
                                borderRadius="8px"
                                fontWeight={500}
                                fontSize="14px"
                                px={4}
                                ml={4}
                              >
                                Delete
                              </Button>
                            </Flex>
                          </CardBody>
                        </Card>
                      ))}
                    </VStack>
                  </VStack>
                )}

                {/* Chat Settings Tab */}
                {selectedTab === 2 && (
                  <VStack spacing={6} align="stretch">
                    <Text fontSize="xl" fontWeight="semibold" color="#000" mb={6}>
                      Chat Settings
                    </Text>
                        <VStack spacing={4} align="stretch">
                          <FormControl>
                            <FormLabel>Chat Name</FormLabel>
                            <Input
                              value={chatSettings.chat_name}
                              onChange={(e) => setChatSettings({ ...chatSettings, chat_name: e.target.value })}
                              bg="rgba(255, 255, 255, 0.8)"
                              border="1px solid rgba(0, 0, 0, 0.1)"
                              _focus={{
                                borderColor: "rgba(0, 0, 0, 0.3)",
                                boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                              }}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel>Chat Color</FormLabel>
                            <Input
                              type="color"
                              value={chatSettings.chat_color}
                              onChange={(e) => setChatSettings({ ...chatSettings, chat_color: e.target.value })}
                              bg="rgba(255, 255, 255, 0.8)"
                              border="1px solid rgba(0, 0, 0, 0.1)"
                              borderRadius="50%"
                              w="60px"
                              h="60px"
                              p={1}
                              cursor="pointer"
                              _focus={{
                                borderColor: "rgba(0, 0, 0, 0.3)",
                                boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                              }}
                              sx={{
                                '&::-webkit-color-swatch-wrapper': {
                                  padding: 0,
                                  borderRadius: '50%',
                                  overflow: 'hidden'
                                },
                                '&::-webkit-color-swatch': {
                                  border: 'none',
                                  borderRadius: '50%'
                                }
                              }}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel>Chat Icon URL</FormLabel>
                            <Input
                              value={chatSettings.chat_icon_url}
                              onChange={(e) => setChatSettings({ ...chatSettings, chat_icon_url: e.target.value })}
                              bg="rgba(255, 255, 255, 0.8)"
                              border="1px solid rgba(0, 0, 0, 0.1)"
                              _focus={{
                                borderColor: "rgba(0, 0, 0, 0.3)",
                                boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                              }}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel>Input Placeholder</FormLabel>
                            <Input
                              value={chatSettings.input_placeholder}
                              onChange={(e) => setChatSettings({ ...chatSettings, input_placeholder: e.target.value })}
                              bg="rgba(255, 255, 255, 0.8)"
                              border="1px solid rgba(0, 0, 0, 0.1)"
                              _focus={{
                                borderColor: "rgba(0, 0, 0, 0.3)",
                                boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                              }}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel>Intro Message</FormLabel>
                            <Textarea
                              value={introMessage}
                              onChange={(e) => setIntroMessage(e.target.value)}
                              rows={3}
                              bg="rgba(255, 255, 255, 0.8)"
                              border="1px solid rgba(0, 0, 0, 0.1)"
                              _focus={{
                                borderColor: "rgba(0, 0, 0, 0.3)",
                                boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                              }}
                            />
                          </FormControl>
                          <Button
                            onClick={handleSaveChatSettings}
                            bg="#000"
                            color="#fff"
                            _hover={{ bg: '#333' }}
                            maxW="200px"
                            alignSelf="flex-start"
                            isLoading={isSaving}
                            loadingText="Saving..."
                            fontWeight={500}
                            fontSize="14px"
                            px={6}
                          >
                            Save Settings
                          </Button>
                        </VStack>
                  </VStack>
                )}

                {/* Instructions Tab */}
                {selectedTab === 3 && (
                  <VStack spacing={6} align="stretch">
                    <Text fontSize="xl" fontWeight="semibold" color="#000" mb={4}>
                      Instructions
                    </Text>
                        <Text fontSize="md" color="#666" mb={4}>
                          Provide instructions for how your AI assistant should behave and respond to users.
                        </Text>
                        <FormControl>
                          <FormLabel>AI Instructions</FormLabel>
                          <Textarea
                            value={instructions}
                            onChange={(e) => setInstructions(e.target.value)}
                            rows={10}
                            placeholder="Enter instructions for your AI assistant..."
                            bg="rgba(255, 255, 255, 0.8)"
                            border="1px solid rgba(0, 0, 0, 0.1)"
                            _focus={{
                              borderColor: "rgba(0, 0, 0, 0.3)",
                              boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                            }}
                          />
                        </FormControl>
                                                  <Button
                            onClick={handleSaveInstructions}
                            bg="#000"
                            color="#fff"
                            _hover={{ bg: '#333' }}
                            maxW="200px"
                            mt={4}
                            isLoading={isSaving}
                            loadingText="Saving..."
                            fontWeight={500}
                            fontSize="14px"
                            px={6}
                          >
                            Save Instructions
                          </Button>
                  </VStack>
                )}

                {/* Embed Widget Tab */}
                {selectedTab === 4 && (
                  <VStack spacing={6} align="stretch">
                    <Text fontSize="xl" fontWeight="semibold" color="#000" mb={4}>
                      Embed Widget
                    </Text>
                    
                    <Card bg="white" border="1px solid #E8EAED" borderRadius="12px">
                      <CardBody p={6}>
                        <Text fontSize="lg" fontWeight="semibold" mb={4} color="#000">
                          📋 Simple Setup
                        </Text>
                        <Text fontSize="md" color="#666" mb={4}>
                          Copy and paste this code snippet before the closing &lt;/body&gt; tag on your website:
                        </Text>
                        
                        <Box position="relative" bg="#f8f9fa" p={4} borderRadius="md" border="1px solid #e9ecef">
                          <pre
                            style={{
                              fontSize: '14px',
                              fontFamily: 'Monaco, Consolas, monospace',
                              lineHeight: '1.4',
                              color: '#333',
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              wordWrap: 'break-word'
                            }}
                          >
{`<script 
  src="https://www.offerbase.co/widget.js" 
  data-site-id="${selectedSite?.id || 'YOUR_SITE_ID'}"
  data-api-url="https://choosly.onrender.com">
</script>`}
                          </pre>
                          <Button 
                            size="sm" 
                            position="absolute" 
                            top={2} 
                            right={2}
                            bg="#007bff"
                            color="white"
                            _hover={{ bg: "#0056b3" }}
                            onClick={() => {
                              const embedCode = `<script 
  src="https://www.offerbase.co/widget.js" 
  data-site-id="${selectedSite?.id}"
  data-api-url="https://choosly.onrender.com">
</script>`;
                              navigator.clipboard.writeText(embedCode);
                              showToast('Embed code copied to clipboard!', 'success');
                            }}
                          >
                            📋 Copy
                          </Button>
                        </Box>
                        
                        <Box bg="#e7f3ff" borderLeft="4px solid #007bff" p={4} mt={4} borderRadius="md">
                          <Text fontWeight="semibold" mb={2} color="#007bff">✨ What happens:</Text>
                          <VStack align="start" spacing={1}>
                            <Text fontSize="sm" color="#666">• Chat widget appears automatically in the bottom-right corner</Text>
                            <Text fontSize="sm" color="#666">• Visitors can chat with your trained AI assistant</Text>
                            <Text fontSize="sm" color="#666">• Uses the same styling you see in this dashboard</Text>
                            <Text fontSize="sm" color="#666">• Perfect style isolation - won't interfere with your site</Text>
                            <Text fontSize="sm" color="#666">• No configuration needed - it just works!</Text>
                          </VStack>
                        </Box>
                      </CardBody>
                    </Card>
                    
                    <Card bg="#f8f9fa" border="1px solid #e9ecef" borderRadius="12px">
                      <CardBody p={4}>
                        <Text fontSize="md" fontWeight="semibold" mb={2} color="#000">
                          🧪 Test Your Widget
                        </Text>
                        <Text fontSize="sm" color="#666" mb={3}>
                          Want to see how it looks? 
                        </Text>
                        <Button
                          size="sm"
                          as="a"
                          href={`https://www.offerbase.co/test-iframe-embed.html?siteId=${selectedSite?.id}&apiUrl=https://choosly.onrender.com`}
                          target="_blank"
                          bg="#28a745"
                          color="white"
                          _hover={{ bg: "#218838" }}
                        >
                          Open Test Page
                        </Button>
                      </CardBody>
                    </Card>
                    
                    <Card bg="#fff3cd" border="1px solid #ffeaa7" borderRadius="12px">
                      <CardBody p={4}>
                        <Text fontSize="md" fontWeight="semibold" mb={2} color="#856404">
                          🆘 Need Help?
                        </Text>
                        <VStack align="start" spacing={1}>
                          <Text fontSize="sm" color="#856404">• The widget loads automatically when your page loads</Text>
                          <Text fontSize="sm" color="#856404">• Place the script before the closing &lt;/body&gt; tag</Text>
                          <Text fontSize="sm" color="#856404">• Check browser console for any error messages</Text>
                          <Text fontSize="sm" color="#856404">• Widget works on all modern browsers</Text>
                        </VStack>
                      </CardBody>
                    </Card>
                  </VStack>
                )}

                {/* Analytics Tab */}
                {selectedTab === 5 && (
                  <VStack spacing={6} align="stretch">
                    <Text fontSize="xl" fontWeight="semibold" color="#000">
                      Analytics Dashboard
                    </Text>
                    
                    {isLoadingAnalytics ? (
                      <Flex justify="center" py={12}>
                        <Spinner size="xl" color="#000" />
                      </Flex>
                    ) : (
                      <>
                        {/* Key Metrics Overview */}
                        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", lg: "repeat(4, 1fr)" }} gap={4}>
                          <Card bg="rgba(59, 130, 246, 0.1)" backdropFilter="blur(10px)" border="1px solid rgba(59, 130, 246, 0.3)" borderRadius="12px">
                            <CardBody textAlign="center" py={6}>
                              <Text fontSize="3xl" fontWeight="bold" color="#3B82F6" mb={1}>
                                {analyticsData.widgetOpens.toLocaleString()}
                              </Text>
                              <Text fontSize="sm" color="#666" fontWeight={500}>
                                Widget Opens
                              </Text>
                              <Text fontSize="xs" color="#999" mt={1}>
                                Last 30 days
                              </Text>
                            </CardBody>
                          </Card>
                          
                          <Card bg="rgba(34, 197, 94, 0.1)" backdropFilter="blur(10px)" border="1px solid rgba(34, 197, 94, 0.3)" borderRadius="12px">
                            <CardBody textAlign="center" py={6}>
                              <Text fontSize="3xl" fontWeight="bold" color="#22C55E" mb={1}>
                                {analyticsData.totalMessages.toLocaleString()}
                              </Text>
                              <Text fontSize="sm" color="#666" fontWeight={500}>
                                Messages Sent
                              </Text>
                              <Text fontSize="xs" color="#999" mt={1}>
                                Last 30 days
                              </Text>
                            </CardBody>
                          </Card>
                          
                          <Card bg="rgba(168, 85, 247, 0.1)" backdropFilter="blur(10px)" border="1px solid rgba(168, 85, 247, 0.3)" borderRadius="12px">
                            <CardBody textAlign="center" py={6}>
                              <Text fontSize="3xl" fontWeight="bold" color="#A855F7" mb={1}>
                                {analyticsData.uniqueUsers.toLocaleString()}
                              </Text>
                              <Text fontSize="sm" color="#666" fontWeight={500}>
                                Unique Users
                              </Text>
                              <Text fontSize="xs" color="#999" mt={1}>
                                Last 30 days
                              </Text>
                            </CardBody>
                          </Card>
                          
                          <Card bg="rgba(249, 115, 22, 0.1)" backdropFilter="blur(10px)" border="1px solid rgba(249, 115, 22, 0.3)" borderRadius="12px">
                            <CardBody textAlign="center" py={6}>
                              <Text fontSize="3xl" fontWeight="bold" color="#F97316" mb={1}>
                                {analyticsData.linkClicks.toLocaleString()}
                              </Text>
                              <Text fontSize="sm" color="#666" fontWeight={500}>
                                Link Clicks
                              </Text>
                              <Text fontSize="xs" color="#999" mt={1}>
                                Last 30 days
                              </Text>
                            </CardBody>
                          </Card>
                        </Grid>

                        {/* Conversion Rate */}
                        <Card bg="white" border="1px solid #E8EAED" borderRadius="12px">
                          <CardBody>
                            <Flex justify="space-between" align="center">
                              <Box>
                                <Text fontSize="lg" fontWeight="semibold" color="#000" mb={1}>
                                  Conversion Rate
                                </Text>
                                <Text fontSize="sm" color="#666">
                                  Percentage of widget opens that resulted in link clicks
                                </Text>
                              </Box>
                              <Box textAlign="right">
                                <Text fontSize="4xl" fontWeight="bold" color={analyticsData.conversionRate > 5 ? "#22C55E" : analyticsData.conversionRate > 2 ? "#F59E0B" : "#EF4444"}>
                                  {analyticsData.conversionRate}%
                                </Text>
                                <Text fontSize="xs" color="#999">
                                  {analyticsData.linkClicks} clicks / {analyticsData.widgetOpens} opens
                                </Text>
                              </Box>
                            </Flex>
                          </CardBody>
                        </Card>

                        {/* Top Performing Links */}
                        <Card bg="white" border="1px solid #E8EAED" borderRadius="12px">
                          <CardBody>
                            <Text fontSize="lg" fontWeight="semibold" color="#000" mb={4}>
                              Top Performing Links
                            </Text>
                            
                            {analyticsData.topLinks.length === 0 ? (
                              <Text color="#666" textAlign="center" py={8}>
                                No link clicks yet. Link performance will appear here once users start clicking your affiliate links.
                              </Text>
                            ) : (
                              <VStack spacing={3} align="stretch">
                                {analyticsData.topLinks
                                  .sort((a, b) => (b.chatClicks + b.externalClicks) - (a.chatClicks + a.externalClicks))
                                  .slice(0, 5)
                                  .map((link) => {
                                    const totalClicks = link.chatClicks + link.externalClicks;
                                    return (
                                      <Card
                                        key={link.id}
                                        bg="rgba(255, 255, 255, 0.85)"
                                        border="1px solid rgba(200, 200, 200, 0.25)"
                                        borderRadius="10px"
                                      >
                                        <CardBody>
                                          <Flex justify="space-between" align="center">
                                            <Flex align="center" gap={3}>
                                              {link.image_url && (
                                                <Image
                                                  src={link.image_url}
                                                  alt={link.name}
                                                  boxSize="40px"
                                                  borderRadius="6px"
                                                  objectFit="cover"
                                                />
                                              )}
                                              <Box>
                                                <Text fontWeight="semibold" color="#000" fontSize="sm">
                                                  {link.name}
                                                </Text>
                                                <Text fontSize="xs" color="#666" noOfLines={1}>
                                                  {link.description}
                                                </Text>
                                              </Box>
                                            </Flex>
                                            <Box textAlign="right">
                                              <Text fontSize="xl" fontWeight="bold" color="#000">
                                                {totalClicks}
                                              </Text>
                                              <Text fontSize="xs" color="#666">
                                                {link.chatClicks} chat + {link.externalClicks} external
                                              </Text>
                                            </Box>
                                          </Flex>
                                        </CardBody>
                                      </Card>
                                    );
                                  })}
                              </VStack>
                            )}
                          </CardBody>
                        </Card>

                        {/* Daily Activity Chart */}
                        <Card bg="white" border="1px solid #E8EAED" borderRadius="12px">
                          <CardBody>
                            <Text fontSize="lg" fontWeight="semibold" color="#000" mb={4}>
                              Daily Activity (Last 30 Days)
                            </Text>
                            
                            {analyticsData.dailyStats.length === 0 ? (
                              <Text color="#666" textAlign="center" py={8}>
                                No activity data yet. Daily statistics will appear here once users start interacting with your widget.
                              </Text>
                            ) : (
                              <Box overflowX="auto">
                                <Flex gap={1} minW="800px" align="end" h="200px" p={4}>
                                  {analyticsData.dailyStats.map((day, index) => {
                                    const maxValue = Math.max(...analyticsData.dailyStats.map(d => Math.max(d.widgetOpens, d.messages, d.linkClicks)));
                                    const widgetHeight = maxValue > 0 ? (day.widgetOpens / maxValue) * 150 : 0;
                                    const messageHeight = maxValue > 0 ? (day.messages / maxValue) * 150 : 0;
                                    const clickHeight = maxValue > 0 ? (day.linkClicks / maxValue) * 150 : 0;
                                    
                                    return (
                                      <VStack key={day.date} spacing={1} flex={1} align="center">
                                        <VStack spacing={0} align="center" h="150px" justify="end">
                                          {clickHeight > 0 && (
                                            <Box
                                              bg="#F97316"
                                              w="8px"
                                              h={`${clickHeight}px`}
                                              borderRadius="2px 2px 0 0"
                                              title={`${day.linkClicks} link clicks`}
                                            />
                                          )}
                                          {messageHeight > 0 && (
                                            <Box
                                              bg="#22C55E"
                                              w="8px"
                                              h={`${messageHeight}px`}
                                              borderRadius={clickHeight > 0 ? "0" : "2px 2px 0 0"}
                                              title={`${day.messages} messages`}
                                            />
                                          )}
                                          {widgetHeight > 0 && (
                                            <Box
                                              bg="#3B82F6"
                                              w="8px"
                                              h={`${widgetHeight}px`}
                                              borderRadius={messageHeight > 0 || clickHeight > 0 ? "0 0 2px 2px" : "2px"}
                                              title={`${day.widgetOpens} widget opens`}
                                            />
                                          )}
                                        </VStack>
                                        <Text fontSize="xs" color="#666" transform="rotate(-45deg)" transformOrigin="center">
                                          {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                        </Text>
                                      </VStack>
                                    );
                                  })}
                                </Flex>
                                
                                {/* Legend */}
                                <Flex justify="center" gap={6} mt={4}>
                                  <Flex align="center" gap={2}>
                                    <Box w="12px" h="12px" bg="#3B82F6" borderRadius="2px" />
                                    <Text fontSize="sm" color="#666">Widget Opens</Text>
                                  </Flex>
                                  <Flex align="center" gap={2}>
                                    <Box w="12px" h="12px" bg="#22C55E" borderRadius="2px" />
                                    <Text fontSize="sm" color="#666">Messages</Text>
                                  </Flex>
                                  <Flex align="center" gap={2}>
                                    <Box w="12px" h="12px" bg="#F97316" borderRadius="2px" />
                                    <Text fontSize="sm" color="#666">Link Clicks</Text>
                                  </Flex>
                                </Flex>
                              </Box>
                            )}
                          </CardBody>
                        </Card>

                        {/* Insights & Tips */}
                        <Card bg="rgba(99, 102, 241, 0.1)" backdropFilter="blur(10px)" border="1px solid rgba(99, 102, 241, 0.3)" borderRadius="12px">
                          <CardBody>
                            <Text fontSize="lg" fontWeight="semibold" color="#6366F1" mb={3}>
                              💡 Analytics Insights
                            </Text>
                            <VStack spacing={2} align="stretch">
                              {analyticsData.conversionRate < 2 && analyticsData.widgetOpens > 10 && (
                                <Text fontSize="sm" color="#666">
                                  • Your conversion rate is below 2%. Consider improving your affiliate link descriptions or adding more relevant products.
                                </Text>
                              )}
                              {analyticsData.conversionRate > 5 && (
                                <Text fontSize="sm" color="#666">
                                  • Excellent conversion rate! Your affiliate links are performing well.
                                </Text>
                              )}
                              {analyticsData.widgetOpens === 0 && (
                                <Text fontSize="sm" color="#666">
                                  • No widget opens yet. Make sure your widget is properly embedded on your website.
                                </Text>
                              )}
                              {analyticsData.totalMessages > analyticsData.widgetOpens * 2 && (
                                <Text fontSize="sm" color="#666">
                                  • Users are highly engaged! They're sending multiple messages per session.
                                </Text>
                              )}
                              <Text fontSize="sm" color="#666">
                                • Analytics data is updated in real-time and shows the last 30 days of activity.
                              </Text>
                            </VStack>
                          </CardBody>
                        </Card>
                      </>
                    )}
                  </VStack>
                )}

                {/* Chat Logs Tab */}
                {selectedTab === 6 && (
                  <VStack spacing={6} align="stretch">
                    <Text fontSize="xl" fontWeight="semibold" color="#000">
                      Chat Logs
                    </Text>
                    
                    {/* Stats Overview */}
                    <HStack spacing={4}>
                      <Card bg="white" border="1px solid #E8EAED" borderRadius="12px" flex={1}>
                        <CardBody textAlign="center">
                          <Text fontSize="2xl" fontWeight="bold" color="#000">{chatStats.totalMessages}</Text>
                          <Text fontSize="sm" color="#666">Total Messages</Text>
                        </CardBody>
                      </Card>
                      <Card bg="white" border="1px solid #E8EAED" borderRadius="12px" flex={1}>
                        <CardBody textAlign="center">
                          <Text fontSize="2xl" fontWeight="bold" color="#000">{chatStats.uniqueSessions}</Text>
                          <Text fontSize="sm" color="#666">Unique Conversations</Text>
                        </CardBody>
                      </Card>
                    </HStack>

                    {/* Chat Sessions List */}
                    <Card bg="white" border="1px solid #E8EAED" borderRadius="12px">
                      <CardBody>
                        <Text fontSize="lg" fontWeight="semibold" color="#000" mb={4}>
                          Recent Conversations
                        </Text>
                        
                        {isLoadingLogs ? (
                          <Flex justify="center" py={8}>
                            <Spinner size="lg" color="#000" />
                          </Flex>
                        ) : chatSessions.length === 0 ? (
                          <Text color="#666" textAlign="center" py={8}>
                            No conversations yet. Chat logs will appear here once users start chatting with your widget.
                          </Text>
                        ) : (
                          <VStack spacing={3} align="stretch">
                            {chatSessions.map((session) => (
                              <Card
                                key={session.session_id}
                                bg="rgba(255, 255, 255, 0.85)"
                                border="1px solid rgba(200, 200, 200, 0.25)"
                                borderRadius="10px"
                                cursor="pointer"
                                _hover={{ bg: "rgba(255, 255, 255, 0.95)", transform: "translateY(-1px)" }}
                                transition="all 0.2s ease"
                                onClick={() => {
                                  setSelectedSession(session);
                                  fetchChatLogs(session.session_id);
                                }}
                              >
                                <CardBody>
                                  <Flex justify="space-between" align="center">
                                    <Box>
                                      <Text fontWeight="semibold" color="#000">
                                        Conversation {session.session_id.slice(-8)}
                                      </Text>
                                      <Text fontSize="sm" color="#666">
                                        {session.message_count} messages • {new Date(session.first_message_at).toLocaleDateString()} {new Date(session.first_message_at).toLocaleTimeString()}
                                      </Text>
                                      {session.user_ip && (
                                        <Text fontSize="xs" color="#999">
                                          IP: {session.user_ip}
                                        </Text>
                                      )}
                                    </Box>
                                    <Button
                                      size="sm"
                                      bg="#000"
                                      color="white"
                                      _hover={{ bg: '#333' }}
                                      fontWeight={500}
                                      fontSize="14px"
                                      px={4}
                                    >
                                      View Chat
                                    </Button>
                                  </Flex>
                                </CardBody>
                              </Card>
                            ))}
                          </VStack>
                        )}
                      </CardBody>
                    </Card>

                    {/* Selected Conversation Details */}
                    {selectedSession && chatLogs.length > 0 && (
                      <Card bg="white" border="1px solid #E8EAED" borderRadius="12px">
                        <CardBody>
                          <Flex justify="space-between" align="center" mb={4}>
                            <Text fontSize="lg" fontWeight="semibold" color="#000">
                              Conversation Details
                            </Text>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedSession(null);
                                setChatLogs([]);
                              }}
                            >
                              Close
                            </Button>
                          </Flex>
                          
                          <VStack spacing={3} align="stretch" maxH="400px" overflowY="auto">
                            {chatLogs.map((log) => (
                              <Box
                                key={log.id}
                                p={3}
                                bg={log.message_type === 'user' ? 'rgba(59, 130, 246, 0.1)' : 'rgba(156, 163, 175, 0.1)'}
                                borderRadius="8px"
                                borderLeft={`4px solid ${log.message_type === 'user' ? '#3B82F6' : '#9CA3AF'}`}
                              >
                                <Flex justify="space-between" align="center" mb={2}>
                                  <Text fontSize="sm" fontWeight="semibold" color={log.message_type === 'user' ? '#3B82F6' : '#6B7280'}>
                                    {log.message_type === 'user' ? 'User' : 'Assistant'}
                                  </Text>
                                  <Text fontSize="xs" color="#999">
                                    {new Date(log.created_at).toLocaleString()}
                                  </Text>
                                </Flex>
                                <Text color="#000" fontSize="sm" lineHeight="1.5">
                                  {log.message_content}
                                </Text>
                                {log.links_shown && log.links_shown.length > 0 && (
                                  <Box mt={2} p={2} bg="rgba(34, 197, 94, 0.1)" borderRadius="6px">
                                    <Text fontSize="xs" color="#059669" fontWeight="semibold" mb={1}>
                                      Links Shown:
                                    </Text>
                                    {log.links_shown.map((link, index) => (
                                      <Text key={index} fontSize="xs" color="#059669">
                                        • {link.name}
                                      </Text>
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </VStack>
                        </CardBody>
                      </Card>
                    )}
                  </VStack>
                )}
              </CardBody>
            </Card>
          </Box>
        </Box>
        {/* The ChatWidget has been replaced with iframe approach below */}
      </Flex>

      {/* Site Selection Modal */}
      <Modal isOpen={siteDialogOpen} onClose={closeSiteDialog}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Manage Sites</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="md" fontWeight="semibold">Your Sites</Text>
              {sites.map((site) => (
                <Flex key={site.id} justify="space-between" align="center" p={3} bg="gray.50" borderRadius="md">
                  <Box>
                    <Text fontWeight="semibold">{site.name}</Text>
                  </Box>
                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      onClick={() => {
                        setSelectedSite(site);
                        closeSiteDialog();
                      }}
                      bg={selectedSite?.id === site.id ? "#000" : "gray.200"}
                      color={selectedSite?.id === site.id ? "white" : "black"}
                      _hover={{
                        bg: selectedSite?.id === site.id ? "#333" : "gray.300",
                        color: selectedSite?.id === site.id ? "white" : "black"
                      }}
                      fontWeight={500}
                      fontSize="14px"
                      px={4}
                    >
                      {selectedSite?.id === site.id ? "Selected" : "Select"}
                    </Button>
                    <IconButton
                      size="sm"
                      icon={<ChakraEditIcon />}
                      onClick={() => handleEditSite(site)}
                      bg="blue.500"
                      color="white"
                      _hover={{ bg: "blue.600" }}
                      aria-label="Edit site"
                      title="Edit site"
                    />
                    <IconButton
                      size="sm"
                      icon={<ChakraDeleteIcon />}
                      onClick={() => handleDeleteSite(site.id)}
                      bg="red.500"
                      color="white"
                      _hover={{ bg: "red.600" }}
                      aria-label="Delete site"
                      isDisabled={sites.length === 1}
                      title={sites.length === 1 ? "Cannot delete the last site" : "Delete site"}
                    />
                  </HStack>
                </Flex>
              ))}
              
              <Divider />
              
              <Text fontSize="md" fontWeight="semibold">Create New Site</Text>
              <FormControl>
                <FormLabel>Site Name</FormLabel>
                <Input
                  value={newSiteName}
                  onChange={(e) => setNewSiteName(e.target.value)}
                  placeholder="Enter site name"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeSiteDialog}>
              Cancel
            </Button>
            <Button
              bg="#000"
              color="white"
              onClick={handleCreateSite}
              isDisabled={!newSiteName.trim()}
              _hover={{ bg: '#333' }}
              fontSize="14px"
            >
              Create Site
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Link Modal */}
      <Modal isOpen={editLinkDialogOpen} onClose={closeEditLinkDialog} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Offer Link</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>URL</FormLabel>
                <Input
                  value={newLink.url}
                  onChange={(e) => setNewLink({ ...newLink, url: e.target.value })}
                  placeholder="https://example.com/product"
                  bg="rgba(255, 255, 255, 0.8)"
                  border="1px solid rgba(0, 0, 0, 0.1)"
                  _focus={{
                    borderColor: "rgba(0, 0, 0, 0.3)",
                    boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                  }}
                />
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Title</FormLabel>
                <Input
                  value={newLink.name}
                  onChange={(e) => setNewLink({ ...newLink, name: e.target.value })}
                  placeholder="Product name"
                  bg="rgba(255, 255, 255, 0.8)"
                  border="1px solid rgba(0, 0, 0, 0.1)"
                  _focus={{
                    borderColor: "rgba(0, 0, 0, 0.3)",
                    boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                  }}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={newLink.description}
                  onChange={(e) => setNewLink({ ...newLink, description: e.target.value })}
                  placeholder="Brief description of the product"
                  rows={3}
                  bg="rgba(255, 255, 255, 0.8)"
                  border="1px solid rgba(0, 0, 0, 0.1)"
                  _focus={{
                    borderColor: "rgba(0, 0, 0, 0.3)",
                    boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                  }}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Image URL</FormLabel>
                <Input
                  value={newLink.image_url}
                  onChange={(e) => setNewLink({ ...newLink, image_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  bg="rgba(255, 255, 255, 0.8)"
                  border="1px solid rgba(0, 0, 0, 0.1)"
                  _focus={{
                    borderColor: "rgba(0, 0, 0, 0.3)",
                    boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                  }}
                />
              </FormControl>
              
              <FormControl>
                <FormLabel>Button Text</FormLabel>
                <Input
                  value={newLink.button_text}
                  onChange={(e) => setNewLink({ ...newLink, button_text: e.target.value })}
                  placeholder="Learn more"
                  bg="rgba(255, 255, 255, 0.8)"
                  border="1px solid rgba(0, 0, 0, 0.1)"
                  _focus={{
                    borderColor: "rgba(0, 0, 0, 0.3)",
                    boxShadow: "0 0 0 1px rgba(0, 0, 0, 0.1)"
                  }}
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button 
              variant="ghost" 
              mr={3} 
              onClick={() => {
                closeEditLinkDialog();
                setEditingLink(null);
                setNewLink({
                  url: '',
                  name: '',
                  description: '',
                  image_url: '',
                  button_text: 'Learn more'
                });
              }}
            >
              Cancel
            </Button>
            <Button
              bg="#000"
              color="white"
              onClick={handleUpdateLink}
              isLoading={isSaving}
              loadingText="Updating..."
              isDisabled={!newLink.url.trim() || !newLink.name.trim()}
              _hover={{ bg: '#333' }}
              fontWeight={500}
              fontSize="14px"
              px={6}
            >
              Update Link
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {selectedSite && (
        <Box
          position="fixed"
          bottom="20px"
          right="20px"
          width="430px"
          maxWidth="90vw"
          maxHeight="80vh"
          height="800px"
          zIndex={999999}
          pointerEvents="none"
        >
          <iframe
            key={`dashboard-${selectedSite.id}`}
            src={`/widget-frame.html?siteId=${selectedSite.id}&apiUrl=${encodeURIComponent(API_URL)}`}
            title="Dashboard Chat Widget"
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              background: 'transparent',
              pointerEvents: 'auto'
            }}
            allow="clipboard-write"
            sandbox="allow-scripts allow-same-origin allow-forms"
          />
        </Box>
      )}
    </>
  );
}

export default Dashboard; 